import {
  AfterContentChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { DynamicFormModel, DynamicFormService, DynamicInputModel, DynamicSelectModel } from '@ng-dynamic-forms/core';
import {
  CmsService,
  GlobalMessageService,
  GlobalMessageType,
  RoutingService,
  TranslationService,
} from '@spartacus/core';
import { BREAKPOINT, BreakpointService } from '@spartacus/storefront';
import { Observable, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { ComponentService } from 'src/app/service/component.service';
import { StandardModalService } from 'src/app/service/modal/standard-modal.service';
import { MyaccountPersonalInfoService } from 'src/app/service/myaccount/myaccount-personal-info.service';
import { StorageService } from 'src/app/service/storage/storage.service';
import { GetStateUserPayload, RequestAccountService } from 'src/app/service/user/request-account.service';

@Component({
  selector: 'app-billing-address-modal',
  templateUrl: './billing-address-modal.component.html',
  styleUrls: ['./billing-address-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class BillingAddressModalComponent implements OnInit, OnDestroy, AfterContentChecked {
  isDesktop: boolean;
  isDisabled = true;
  formGroup: FormGroup;
  billingAddressFormModel: DynamicFormModel;
  currentPageSubscription$: Subscription;
  billingAddressFormModelSubscription$: Subscription;
  billingAddressFormGroupSubscription$: Subscription;
  errorMessage: any;
  postalCodeErrorMessage: any;
  isocodeShort: string;
  countryIsoCodeData: string;
  count = 0;
  stateData$: Observable<any>;
  postalCodeInput: DynamicInputModel;
  lastNameInput: DynamicInputModel;
  firstNameInput: DynamicInputModel;
  countrySelect: DynamicSelectModel<string>;
  line1Input: DynamicInputModel;
  line2Input: DynamicInputModel;
  postalInput: DynamicInputModel;
  cityInput: DynamicInputModel;
  stateInput: DynamicInputModel;
  countyInput: DynamicInputModel;
  selectedCountry: DynamicSelectModel<string>;
  selectedState: DynamicSelectModel<string>;
  buttonNoUpdate = false;
  cxTabletWidth = this.breakpointService.getSize(BREAKPOINT.lg);
  constructor(
    protected modalService: StandardModalService,
    private formService: DynamicFormService,
    private componentService: ComponentService,
    private cmsService: CmsService,
    private personalInfoSvc: MyaccountPersonalInfoService,
    private requestAccountService: RequestAccountService,
    private routingService: RoutingService,
    private messageService: GlobalMessageService,
    protected translation: TranslationService,
    private cdr: ChangeDetectorRef,
    private breakpointService: BreakpointService,
    private storageService: StorageService
  ) {
    this.modalService
      .getModalData()
      .pipe(take(1))
      .subscribe((res) => {
        this.isDesktop = res.isDesktop;
        this.isDisabled = res.isDisabled;
      });
  }

  ngOnInit() {
    this.currentPageSubscription$ = this.cmsService.getCurrentPage().subscribe((currentPage) => {
      if (currentPage && currentPage.pageId.includes('accountPersonalInfoPage')) {
        this.billingAddressFormModelSubscription$ = this.componentService.dynamicFormModel$.subscribe(
          (billingAddressFormModel) => {
            this.updateFormModel(billingAddressFormModel);
          }
        );
        this.billingAddressFormGroupSubscription$ = this.componentService.dynamicFormGroup$.subscribe(
          (billingAddressFormGroup) => {
            this.updateFormGroup(billingAddressFormGroup);
            this.getBillingAddressData();
          }
        );
      }
    });

    this.formService.detectChanges();
  }

  updateFormModel(formModel1: any) {
    this.billingAddressFormModel = formModel1;
    this.firstNameInput = this.formService.findModelById<DynamicInputModel>(
      'firstName_full',
      this.billingAddressFormModel
    );

    if (this.firstNameInput !== null) {
      this.translation
        .translate(`error.dynamicform.firstName_full`)
        .pipe(take(1))
        .subscribe((errorText) => (this.errorMessage = errorText));
      const hasError = {
        required: this.errorMessage,
      };
      this.firstNameInput.errorMessages = hasError;
    }

    this.lastNameInput = this.formService.findModelById<DynamicInputModel>(
      'lastName_full',
      this.billingAddressFormModel
    );

    if (this.lastNameInput !== null) {
      this.translation
        .translate(`error.dynamicform.lastName_full`)
        .pipe(take(1))
        .subscribe((errorText) => (this.errorMessage = errorText));
      const hasError = {
        required: this.errorMessage,
      };
      this.lastNameInput.errorMessages = hasError;
    }

    this.countrySelect = this.formService.findModelById<DynamicSelectModel<string>>(
      'deliveryCountry',
      this.billingAddressFormModel
    );
    if (this.countrySelect !== null) {
      this.translation
        .translate(`error.dynamicform.deliveryCountry`)
        .pipe(take(1))
        .subscribe((errorText) => (this.errorMessage = errorText));
      const hasError = {
        required: this.errorMessage,
      };
      this.countrySelect.errorMessages = hasError;
    }

    this.line1Input = this.formService.findModelById<DynamicInputModel>('addressLine1', this.billingAddressFormModel);
    if (this.line1Input !== null) {
      this.translation
        .translate(`error.dynamicform.addressLine1`)
        .pipe(take(1))
        .subscribe((errorText) => (this.errorMessage = errorText));
      const hasError = {
        required: this.errorMessage,
      };
      this.line1Input.errorMessages = hasError;
      this.line1Input.maxLength = 255;
    }
    this.line2Input = this.formService.findModelById<DynamicInputModel>('addressLine2', this.billingAddressFormModel);
    if (this.line2Input !== null) {
      this.translation
        .translate(`error.dynamicform.addressLine2`)
        .pipe(take(1))
        .subscribe((errorText) => (this.errorMessage = errorText));
      const hasError = {
        required: this.errorMessage,
      };
      this.line2Input.errorMessages = hasError;
      this.line2Input.maxLength = 255;
    }

    this.postalInput = this.formService.findModelById<DynamicInputModel>('postalCode', this.billingAddressFormModel);
    if (this.postalInput !== null) {
      this.translation
        .translate(`error.dynamicform.postalCode`)
        .pipe(take(1))
        .subscribe((errorText) => (this.errorMessage = errorText));
      this.translation
        .translate(`error.dynamicform.postalCode_valid`)
        .pipe(take(1))
        .subscribe((errorText) => (this.postalCodeErrorMessage = errorText));
      const hasError = {
        required: this.errorMessage,
        pattern: this.postalCodeErrorMessage,
      };

      this.postalInput.errorMessages = hasError;
    }
    this.cityInput = this.formService.findModelById<DynamicInputModel>('city', this.billingAddressFormModel);
    if (this.cityInput !== null) {
      this.translation
        .translate(`error.dynamicform.city`)
        .pipe(take(1))
        .subscribe((errorText) => (this.errorMessage = errorText));
      const hasError = {
        required: this.errorMessage,
      };

      this.cityInput.errorMessages = hasError;
    }

    this.stateInput = this.formService.findModelById<DynamicInputModel>('state', this.billingAddressFormModel);
    if (this.stateInput !== null) {
      this.translation
        .translate(`error.dynamicform.state`)
        .pipe(take(1))
        .subscribe((errorText) => (this.errorMessage = errorText));
      const hasError = {
        required: this.errorMessage,
      };

      this.stateInput.errorMessages = hasError;
    }

    this.countyInput = this.formService.findModelById<DynamicInputModel>('county', this.billingAddressFormModel);
    if (this.countyInput !== null) {
      this.translation
        .translate(`error.dynamicform.county`)
        .pipe(take(1))
        .subscribe((errorText) => (this.errorMessage = errorText));
      const hasError = {
        required: this.errorMessage,
      };

      this.countyInput.errorMessages = hasError;
    }

    this.formService.detectChanges();
  }

  getBillingAddressData(): void {
    if (
      this.storageService.getSessionStorageItem('country').localeCompare('undefined') === 0 ||
      this.storageService.getSessionStorageItem('country').localeCompare('null') === 0
    ) {
      this.formGroup.get('deliveryCountry').setValue(null);
    } else {
      this.formGroup.get('deliveryCountry').setValue(this.storageService.getSessionStorageItem('country'));
    }
    if (
      this.storageService.getSessionStorageItem('line1').localeCompare('undefined') === 0 ||
      this.storageService.getSessionStorageItem('line1').localeCompare('null') === 0
    ) {
      this.formGroup.get('addressLine1').setValue(null);
    } else {
      this.formGroup.get('addressLine1').setValue(this.storageService.getSessionStorageItem('line1'));
    }

    if (
      this.storageService.getSessionStorageItem('line2').localeCompare('undefined') === 0 ||
      this.storageService.getSessionStorageItem('line2').localeCompare('null') === 0
    ) {
      this.formGroup.get('addressLine2').setValue(null);
    } else {
      this.formGroup.get('addressLine2').setValue(this.storageService.getSessionStorageItem('line2'));
    }

    if (
      this.storageService.getSessionStorageItem('postalcode').localeCompare('undefined') === 0 ||
      this.storageService.getSessionStorageItem('postalcode').localeCompare('null') === 0
    ) {
      this.formGroup.get('postalCode').setValue(null);
    } else {
      this.formGroup.get('postalCode').setValue(this.storageService.getSessionStorageItem('postalcode'));
    }

    if (
      this.storageService.getSessionStorageItem('city').localeCompare('undefined') === 0 ||
      this.storageService.getSessionStorageItem('city').localeCompare('null') === 0
    ) {
      this.formGroup.get('city').setValue(null);
    } else {
      this.formGroup.get('city').setValue(this.storageService.getSessionStorageItem('city'));
    }

    if (
      this.storageService.getSessionStorageItem('state').localeCompare('undefined') === 0 ||
      this.storageService.getSessionStorageItem('state').localeCompare('null') === 0
    ) {
      this.formGroup.get('state').setValue(null);
    } else {
      this.formGroup.get('state').setValue(this.storageService.getSessionStorageItem('state'));
    }
    if (
      this.storageService.getSessionStorageItem('county').localeCompare('undefined') === 0 ||
      this.storageService.getSessionStorageItem('county').localeCompare('null') === 0
    ) {
      this.formGroup.get('county').setValue(null);
    } else {
      this.formGroup.get('county').setValue(this.storageService.getSessionStorageItem('county'));
    }

    if (
      this.storageService.getSessionStorageItem('fname').localeCompare('undefined') === 0 ||
      this.storageService.getSessionStorageItem('fname').localeCompare('null') === 0
    ) {
      this.formGroup.get('firstName_full').setValue(null);
    } else {
      this.formGroup.get('firstName_full').setValue(this.storageService.getSessionStorageItem('fname'));
    }

    if (
      this.storageService.getSessionStorageItem('lname').localeCompare('undefined') === 0 ||
      this.storageService.getSessionStorageItem('lname').localeCompare('null') === 0
    ) {
      this.formGroup.get('lastName_full').setValue(null);
    } else {
      this.formGroup.get('lastName_full').setValue(this.storageService.getSessionStorageItem('lname'));
    }
    this.formService.detectChanges();
  }
  updateFormGroup(formGroup: any): void {
    this.formGroup = formGroup;
    this.postalCodeInput = this.formService.findModelById<DynamicInputModel>(
      'postalCode',
      this.billingAddressFormModel
    );

    this.selectedCountry = this.formService.findModelById<DynamicSelectModel<string>>(
      'deliveryCountry',
      this.billingAddressFormModel
    );

    /* State */
    const optionStateValues: any = [];
    if (this.count === 0) {
      if (this.storageService.getSessionStorageItem('country') === '') {
        this.countryIsoCodeData = this.formGroup.get('deliveryCountry').value;
      } else {
        this.countryIsoCodeData = this.formGroup.get('deliveryCountry').value;
        if (this.countryIsoCodeData === null) {
          this.countryIsoCodeData = this.storageService.getSessionStorageItem('country');
        }
      }
    } else {
      this.countryIsoCodeData = this.formGroup.get('deliveryCountry').value;
    }
    const getStateUserPayload: GetStateUserPayload = {
      fields: 'DEFAULT',
      countryIsoCode: this.countryIsoCodeData,
    };
    this.stateData$ = this.requestAccountService.getStateForRequestAccount(getStateUserPayload);
    this.stateData$.pipe(take(1)).subscribe((data) => {
      if (data.items.length > 0) {
        this.count = data.items.length;
        for (const item of data.items) {
          const options = {
            label: item.name,
            value: item.code,
          };
          optionStateValues.push(options);
        }
      } else {
        this.count = 0;
      }
      this.selectedState = this.formService.findModelById<DynamicSelectModel<string>>(
        'state',
        this.billingAddressFormModel
      );
      if (this.selectedState !== null) {
        this.selectedState.options = optionStateValues;
        this.formService.detectChanges();
      }
    });
    /* State */
    this.formService.detectChanges();
    this.disablingButton(this.formGroup);
  }

  disablingButton(billingAddressGroup: FormGroup) {
    billingAddressGroup.valueChanges.subscribe((data) => {
      if (this.checkFormValues(data, true)) {
        this.buttonNoUpdate = false;
        if (this.storageService.getSessionStorageItem('state') === 'null') {
          if (this.checkFormValues(data, false)) {
            this.buttonNoUpdate;
          } else {
            this.buttonNoUpdate = true;
          }
        } else {
        }
      } else {
        this.buttonNoUpdate = true;
      }
    });
  }

  checkFormValues(data: any, withState: boolean): boolean {
    var changingValue =
      data.firstName_full !== this.storageService.getSessionStorageItem('fname') ||
      data.lastName_full !== this.storageService.getSessionStorageItem('lname') ||
      data.deliveryCountry !== this.storageService.getSessionStorageItem('country') ||
      data.addressLine1 !== this.storageService.getSessionStorageItem('line1') ||
      data.postalCode !== this.storageService.getSessionStorageItem('postalcode') ||
      data.city !== this.storageService.getSessionStorageItem('city') ||
      data?.county !== this.storageService.getSessionStorageItem('county') ||
      data?.addressLine2 !== this.storageService.getSessionStorageItem('line2');

    if (withState) {
      return changingValue || (data.state && data.state !== this.storageService.getSessionStorageItem('state'));
    }

    if (!withState) {
      return changingValue;
    }
  }

  onSubmit(): void {
    if (typeof this.formGroup !== 'undefined') {
      if (this.formGroup.status.toLowerCase() === 'valid') {
        const isocodeShortState = this.formGroup.value.state;
        if (isocodeShortState !== null) {
          this.isocodeShort = isocodeShortState.split('-');
        }
        const billingAddressPayload: any = {
          // const billingAddressPayload: BillingAddressWsDTO = {
          firstName: this.formGroup.value.firstName_full,
          lastName: this.formGroup.value.lastName_full,
          district: this.formGroup.value.county,
          line1: this.formGroup.value.addressLine1,
          line2: this.formGroup.value.addressLine2,
          country: {
            isocode: this.formGroup.value.deliveryCountry,
          },
          region: {
            countryIso: this.formGroup.value.deliveryCountry,
            isocode: this.formGroup.value.state,
            isocodeShort: isocodeShortState !== null ? this.isocodeShort[1] : null,
          },
          postalCode: this.formGroup.value.postalCode,
          town: this.formGroup.value.city,
        };

        Object.keys(billingAddressPayload).forEach(
          (key: any) => billingAddressPayload[key] === null && delete billingAddressPayload[key]
        );
        this.personalInfoSvc
          .setBillingAddress(billingAddressPayload)
          .pipe(take(1))
          .subscribe((data) => {
            this.closeModal({ isPending: true });
            if (typeof data !== 'undefined') {
              this.storageService.removeSessionStorageItem('onbilling');
              this.storageService.removeSessionStorageItem('country');
              this.storageService.removeSessionStorageItem('line1');
              this.storageService.removeSessionStorageItem('line2');
              this.storageService.removeSessionStorageItem('postalcode');
              this.storageService.removeSessionStorageItem('city');
              this.storageService.removeSessionStorageItem('state');
              this.storageService.removeSessionStorageItem('statename');
              this.storageService.removeSessionStorageItem('county');
              this.storageService.removeSessionStorageItem('fname');
              this.storageService.removeSessionStorageItem('lname');
              this.routingService.goByUrl('/my-account/account-information');
              this.messageService.add(
                {
                  key: 'myAccount.successBillingAddress',
                },
                GlobalMessageType.MSG_TYPE_CONFIRMATION
              );
            }
          });
      }
    }
  }

  ngAfterContentChecked() {
    this.cdr.detectChanges();
  }

  closeModal(info?: any) {
    this.modalService.closeDialog(info);
  }

  ngOnDestroy() {
    if (this.currentPageSubscription$) {
      this.currentPageSubscription$.unsubscribe();
    }
    if (this.billingAddressFormModelSubscription$) {
      this.billingAddressFormModelSubscription$.unsubscribe();
    }
    if (this.billingAddressFormGroupSubscription$) {
      this.billingAddressFormGroupSubscription$.unsubscribe();
    }
  }
}
